/**
 * useSpotlight Hook
 *
 * Manages first-time user spotlight card visibility via localStorage.
 * Keys are stored as `sidestreet_spotlight_{id}` with value "1" when dismissed.
 *
 * Returns:
 *   isVisible(id)  — true if the card has not been dismissed yet
 *   dismiss(id)    — marks a card as dismissed (persists across sessions)
 *   resetAll()     — clears all spotlight dismissals ("Show me around again")
 */

import { useState, useCallback, useEffect } from 'react'

const KEY_PREFIX = 'sidestreet_spotlight_'
const RESET_EVENT = 'sidestreet_spotlight_reset'

function getKey(id) {
  return `${KEY_PREFIX}${id}`
}

export function useSpotlight() {
  // Track dismissed state in component state so dismissals re-render immediately
  const [dismissed, setDismissed] = useState(() => {
    const keys = {}
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k && k.startsWith(KEY_PREFIX)) {
          const id = k.slice(KEY_PREFIX.length)
          keys[id] = true
        }
      }
    } catch (_) {}
    return keys
  })

  const isVisible = useCallback(
    (id) => !dismissed[id],
    [dismissed]
  )

  const dismiss = useCallback((id) => {
    try {
      localStorage.setItem(getKey(id), '1')
    } catch (_) {}
    setDismissed((prev) => ({ ...prev, [id]: true }))
  }, [])

  const resetAll = useCallback(() => {
    try {
      const toRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k && k.startsWith(KEY_PREFIX)) toRemove.push(k)
      }
      toRemove.forEach((k) => localStorage.removeItem(k))
    } catch (_) {}
    setDismissed({})
    try {
      window.dispatchEvent(new CustomEvent(RESET_EVENT))
    } catch (_) {}
  }, [])

  // Keep multiple hook instances in sync (header menu + page spotlight cards).
  useEffect(() => {
    const syncFromStorage = () => {
      setDismissed(() => {
        const keys = {}
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i)
            if (k && k.startsWith(KEY_PREFIX)) {
              const id = k.slice(KEY_PREFIX.length)
              keys[id] = true
            }
          }
        } catch (_) {}
        return keys
      })
    }
    const onReset = () => setDismissed({})
    window.addEventListener(RESET_EVENT, onReset)
    window.addEventListener('storage', syncFromStorage)
    return () => {
      window.removeEventListener(RESET_EVENT, onReset)
      window.removeEventListener('storage', syncFromStorage)
    }
  }, [])

  return { isVisible, dismiss, resetAll }
}
